import React from 'react'
import CheckRounded from '@mui/icons-material/CheckRounded'
import ReplayRounded from '@mui/icons-material/ReplayRounded'
import HourglassTopRounded from '@mui/icons-material/HourglassTopRounded'
import StarRounded from '@mui/icons-material/StarRounded'
import { categoryMeta, FallbackIcon } from '../constants'
import StarRating from './StarRating'
import { SCORE_MAX, clampScore, formatScore, scoreTier } from './scoreUtils'
import { STATUS_COLOR_VAR, TYPE_COLOR_VAR, isQuestActive, normalizeQuestStatus } from './questTypes'

/**
 * @param {'left'|'right'|'below'} labelSide
 */
export default function QuestNode({
  item,
  status = 'unsubmitted',
  style,
  onClick,
  hiddenCount = 0,
  labelSide = 'below',
  celebrate = false,
  milestone = false,
  pulse = false,
}) {
  const meta = categoryMeta[item.assetType] || {
    label: item.assetType || '资源',
    Icon: FallbackIcon,
  }
  const Icon = meta.Icon
  const typeAccent = TYPE_COLOR_VAR[item.assetType] || '#6f9fff'
  const statusAccent = STATUS_COLOR_VAR[status] || typeAccent
  const accent = status === 'selected' ? typeAccent : statusAccent
  const title = (item.title || '未命名').trim()
  const active = isQuestActive(item)
  const evaluated = normalizeQuestStatus(
    item?.progress?.status,
    item?.assignmentStatus || item?.progress?.assignmentStatus,
  ) === 'evaluated' || status === 'evaluated'
  const score = evaluated ? clampScore(item?.evaluationSummary?.score) : null
  const tier = score == null ? '' : scoreTier(score)
  const delta = score == null ? null : Number(item?.evaluationSummary?.deltaFromPrevious)
  const hasDelta = Number.isFinite(delta) && Math.abs(delta) >= 0.05
  const shownScore = score
  const scoreLabel = score == null ? '' : `，得分 ${formatScore(score)} 分（满分 ${SCORE_MAX}）`
  const rejected = status === 'rejected'
  const label = `${meta.label} ${title}${active ? '（当前应做，开始挑战）' : ''}${scoreLabel}`

  return (
    <button
      type="button"
      className={`quest-node status-${status} label-${labelSide}${active ? ' is-current' : ''}${tier ? ` score-${tier}` : ''}${milestone ? ' is-milestone' : ''}${rejected ? ' is-repair' : ''}${pulse ? ' is-pulse' : ''}`}
      style={{ ...style, '--quest-accent': tier ? undefined : accent }}
      onClick={onClick}
      aria-label={label.trim()}
      aria-pressed={status === 'selected'}
      data-quest-key={item.key}
    >
      <span className="quest-node-anchor">
        <span className="quest-node-shadow" aria-hidden="true" />
        {active ? <span className="quest-node-current-ring" aria-hidden="true" /> : null}
        {active ? <span className="quest-node-current-ring is-outer" aria-hidden="true" /> : null}
        {tier === 'perfect' ? <span className="quest-node-flag" aria-hidden="true" /> : null}
        <span className="quest-node-disc">
          <Icon className="quest-node-icon" />
          {status === 'evaluated' || (evaluated && status === 'selected') ? <CheckRounded className="quest-node-check" /> : null}
          {status === 'submitted' ? <HourglassTopRounded className="quest-node-badge-icon" /> : null}
          {status === 'rejected' ? <ReplayRounded className="quest-node-badge-icon is-rejected" /> : null}
        </span>
        {item.isHighlight ? (
          <span className="quest-node-star" title="推荐" aria-hidden="true">
            <StarRounded />
          </span>
        ) : null}
        {hiddenCount > 0 ? <span className="quest-node-more">+{hiddenCount}</span> : null}
        {active ? <span className="quest-node-here">当前应做</span> : null}
        {score != null ? (
          <span className="quest-node-score">
            <StarRating score={shownScore} animate={celebrate} />
            <span className="quest-node-score-num">{formatScore(score)}</span>
            {hasDelta ? (
              <span className={`quest-node-delta${delta < 0 ? ' is-down' : ''}`}>
                {delta > 0 ? '↑' : '↓'}
              </span>
            ) : null}
          </span>
        ) : null}
        <span className="quest-node-title">
          <span className="quest-node-type">{meta.label}</span>
          <span className="quest-node-name">{title}</span>
        </span>
      </span>
    </button>
  )
}
